import { ModalName } from '@share/enums';
import { PaymentMethod } from '@share/interfaces';
import { market } from '@state/market';
import { showPercentage, toggleBootstrapModal } from '@util/helper';
import { useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DotLoading from '@com/utils/DotLoading';
import PaymentRow from '@com/utils/PaymentRow';

interface Params {
  collectionAddress: string;
  tokenId: number;
  price: number;
  currencyId: number;
  royalty: number;
  orderId: number;
  paymentMethod: PaymentMethod;
  loadLaunchpadData: () => void;
}

function TakeHighestBidModal({
  collectionAddress,
  tokenId,
  price,
  currencyId,
  royalty,
  orderId,
  paymentMethod,
  loadLaunchpadData,
}: Params): JSX.Element {
  /* Define state / hook */
  const { getCurrencyNameById } = useCurrency();

  const { takeHighestBid, isSigned, payment } = market.useContainer();
  const { t } = useTranslation();
  const { displayCurrency } = useHelper();

  /* Custom Data Object for render or other usages */
  const [isLoading, setIsLoading] = useState(false);
  const currencyName = getCurrencyNameById(currencyId);

  /* Render control function / logic */
  const getRoyaltyFee = (amount: number) => {
    return Number(amount) * (royalty / 100);
  };

  const getServiceFee = (amount: number) => {
    return Number(amount) * (Number(paymentMethod?.marketplace_fee) / 100);
  };

  const getHandlingFee = (amount: number) => {
    return Number(amount) * (Number(paymentMethod?.payment_fee) / 100);
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  const handleTakeHighest = useDebouncedCallback(
    async () => {
      const isLogin = await payment();
      if (!isLogin) return;
      setIsLoading(true);

      const isTxSuccess = await takeHighestBid(
        collectionAddress,
        tokenId,
        currencyId,
        orderId
      );
      setIsLoading(false);
      if (isTxSuccess) toggleBootstrapModal(ModalName.TAKE_HIGHEST);
      loadLaunchpadData();
    },
    500,
    { leading: true, trailing: false }
  );

  /* Styling */

  /* Render */
  return (
    <div
      aria-hidden
      className="modal fade popup"
      id={ModalName.TAKE_HIGHEST}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <button
            className="close"
            type="button"
            onClick={() => {
              if (!isLoading) toggleBootstrapModal(ModalName.TAKE_HIGHEST);
            }}
          >
            <span aria-hidden>&times;</span>
          </button>
          <div className="modal-body space-y-20 pd-40">
            <h3>{t('modal.takeHighestBid')}</h3>
            <hr />
            {isLoading ? (
              <>
                <DotLoading />
                <div className="row mt-4">
                  <div
                    className="col-12 text-center mt-5"
                    style={{ fontSize: '2.5em' }}
                  >
                    {isSigned
                      ? t('modal.waitContract')
                      : t('modal.signContract')}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="d-flex justify-content-between mt-5 mb-3">
                  <p>{t('modal.highestBidNow')}</p>
                  <div className="text-right mt-1">
                    <div className="price color-popup">
                      {displayCurrency(price, currencyName)}
                    </div>
                  </div>
                </div>

                <PaymentRow
                  currency={currencyName}
                  price={getRoyaltyFee(price)}
                  title={t('modal.royalty', {
                    percentage: showPercentage(royalty),
                  })}
                />

                <PaymentRow
                  currency={currencyName}
                  price={getServiceFee(price)}
                  title={t('modal.serviceFee', {
                    percentage: showPercentage(paymentMethod?.marketplace_fee),
                  })}
                />

                <PaymentRow
                  currency={currencyName}
                  price={getHandlingFee(price)}
                  title={t('modal.handlingFee', {
                    percentage: showPercentage(paymentMethod?.payment_fee),
                  })}
                />

                <div className="d-flex justify-content-between mb-5 mt-3">
                  <p>{t('modal.youWillReceive')}</p>
                  <div className="text-right mt-1">
                    <div className="price color-popup">
                      {displayCurrency(
                        price -
                          getRoyaltyFee(price) -
                          getServiceFee(price) -
                          getHandlingFee(price),
                        currencyName
                      )}
                    </div>
                  </div>
                </div>

                <button
                  aria-label={t('modal.takeHighestBid')}
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    handleTakeHighest();
                  }}
                >
                  {t('modal.takeHighestBid')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TakeHighestBidModal;
